import axios from 'axios';

const api = axios.create({ baseURL: '', withCredentials: true });

export interface User {
  id: string;
  email: string;
  display_name: string | null;
  referral_code: string | null;
  wallet_currency?: string;   // "ZAR" | "USD"; backend always sends it
  /** v4-9-2: whether the user has verified their email (via the OTP flow). Drives
   *  the proactive "verify your email" nudge AND the entry gate. Defaults true for
   *  grandfathered users; only false for accounts created after the gate landed. */
  is_email_verified?: boolean;
}

export interface DrawProgress {
  threshold_pct: number;
  remaining_count: number;
  mode: 'hidden' | 'percentage' | 'urgency' | 'complete';
}

// ── Skill-based competitions (v4-epic-4) ──────────────────────────────────────
/**
 * Skill mechanic that gates a competition's winner selection.
 *  - "none"            → pure random draw (legacy behaviour)
 *  - "closest_guess"   → entrant submits a number; closest to the answer wins
 *  - "spot_difference" → entrant taps the differences between two images
 *  - "caption_this"    → caption contest (admin UI + entrant game ship later, v4-4-7)
 *  - "rank_it"         → ranking contest (admin UI + entrant game ship later, v4-4-8)
 * The admin form currently only exposes none/closest_guess/spot_difference — the
 * remaining two exist in the backend but have no entrant UI yet.
 */
export type SkillType =
  | 'none'
  | 'closest_guess'
  | 'spot_difference'
  | 'caption_this'
  | 'rank_it';

/** skill-tiebreak-1: how an equal-top-score tie is broken for a skill competition,
 *  chosen by the admin at campaign time. Both modes are skill-determined (never
 *  random). "submission_order" = earliest submission wins (default, back-compat);
 *  "reaction_speed" = fastest server-measured answer wins. */
export type TiebreakMode = 'submission_order' | 'reaction_speed';

export interface Competition {
  id: string;
  title: string;
  /** Friendly SEO slug for the public /win/<slug> URL; null on legacy rows. */
  slug?: string | null;
  prize_description: string;
  prize_media_urls: string[];
  video_asset?: CompetitionVideoAssetPublic | null;
  /** mktg-student-village: themed collection/tile tags (e.g. ["student-village",
   *  "wsu"]). Drives the Student Village grouping. Defaults to []. */
  tags?: string[];
  entry_fee_cents: number;
  currency_code: string;
  /** v4-7-2: the prize's factual retail/in-app value, in its OWN currency.
   *  null on legacy rows the backfill couldn't parse. */
  prize_value_cents?: number | null;
  /** v4-7-2: ISO-4217 currency for prize_value_cents (falls back to the entry-fee
   *  currency server-side when unset). null when there is no prize value. */
  prize_value_currency_code?: string | null;
  draw_date: string;
  status: string;
  max_entries_per_user?: number;
  terms_markdown?: string;
  draw_progress?: DrawProgress;
  // ── Skill mechanic (v4-epic-4) — present on public competition detail ──
  /** Defaults to "none" on legacy / random competitions. */
  skill_type?: SkillType;
  /** Public skill config — the answer key is stripped server-side. Shape varies
   *  by skill_type (see SkillConfig* interfaces below). null when skill_type is "none". */
  skill?: Record<string, unknown> | null;
  /** ISO datetime — entrants must submit their skill answer before this. */
  skill_submission_deadline?: string | null;
  /** skill-tiebreak-1: how an equal-top-score tie is resolved. "submission_order"
   *  (default, earliest submission wins) or "reaction_speed" (fastest server-measured
   *  answer wins). The entry flow fetches a signed skill-challenge token when this is
   *  "reaction_speed" so the answer can be timed. Both modes are skill-determined. */
  tiebreak_mode?: TiebreakMode;
  // ── Fulfilment (corp-1-1) — per-competition cash/fulfilment copy ──
  /** True (default) = we may pay the cash equivalent. False = non-cash prize
   *  (corporate-sponsored). Drives the entrant cash-payable note. */
  cash_equivalent_allowed?: boolean;
  /** Per-competition fulfilment note. Overrides the default cash-payable copy.
   *  Was a hardcoded global string (hotfix-1-165); now per-competition. */
  fulfilment_note?: string | null;
  /** corp-1-3: true when this competition is sponsored (corporate campaign link).
   *  Drives the entrant "share my details with the sponsor" consent checkbox. No
   *  sponsor identity/PII is exposed — just the boolean fact. */
  is_sponsored?: boolean;
  /** hotfix-1-178: co-branding for a competition run under a corporate agreement —
   *  the "In partnership with X" marker. Present only when a CampaignSponsor exists
   *  (null/absent for ordinary house-run competitions). Co-branding only, no PII. */
  sponsor?: CompetitionSponsor | null;
  // Legacy fields kept for backward compat with existing card/detail views
  unique_participant_count?: number;
}

/** hotfix-1-178: public co-branding for a corporate-sponsored competition. */
export interface CompetitionSponsor {
  /** The partner's display name, e.g. "BMW Fourways". */
  display_name: string;
  /** Optional partner logo URL (hosted by corporate-partnerships). */
  logo_url?: string | null;
  /** Optional partner brand colour, e.g. "#1C69D4". */
  brand_color?: string | null;
}

export interface CompetitionVideoAssetPublic {
  video_url: string;
  poster_url: string;
  provider: string;
  duration_seconds?: number | null;
  aspect_ratio?: string | null;
  mime_type: string;
}

// ── Skill config shapes (admin-authored; backend is source of truth) ──────────
/** closest_guess admin config — includes the hidden answer (correct_value). */
export interface ClosestGuessConfig {
  question: string;
  image_url?: string;
  unit?: string;
  /** The correct answer — hidden from entrants in the public `skill` payload. */
  correct_value: number;
}

/** A single difference hotspot. Coordinates are normalised to 0..1. */
export interface SpotDifferenceSpot {
  x: number;
  y: number;
  radius: number;
}

/** spot_difference admin config — `differences` are the answer key. */
export interface SpotDifferenceConfig {
  instructions?: string;
  image_a_url: string;
  image_b_url: string;
  required_count: number;
  differences: SpotDifferenceSpot[];
}

/** A single PUBLISHED judging criterion for caption_this. Weights sum to 100.
 *  There is no hidden answer key — criteria are entrant-visible. */
export interface CaptionCriterion {
  label: string;
  weight: number;
  description: string;
}

/** caption_this admin config — judging criteria are published (nothing hidden). */
export interface CaptionThisConfig {
  image_url: string;
  prompt: string;
  max_chars: number;
  criteria: CaptionCriterion[];
}

export interface RegisterPayload {
  email: string;
  password: string;
  display_name?: string;
  id_type: 'sa_id' | 'passport';
  id_number: string;
  date_of_birth?: string;
  mobile_number: string;
  country_code: string;
  province?: string;
  used_referral_code?: string;
}

export const authRegister = (payload: RegisterPayload) =>
  api.post<User>('/api/v1/auth/register', payload);

export const authLogin = (email: string, password: string) =>
  api.post<User>('/api/v1/auth/login', { email, password });

export const authLogout = () => api.post('/api/v1/auth/logout');

export const authMe = () => api.get<User>('/api/v1/auth/me');

// ── Email verification OTP (v4-9-2) ────────────────────────────────────────────
/** Response of POST /api/v1/auth/request-email-verification. `sent` is true once
 *  the 6-digit code email is handed to SMTP; `already` is true when the user was
 *  already verified (nothing to send). On a mail failure the backend returns 503. */
export interface RequestEmailVerificationResponse {
  sent: boolean;
  already?: boolean;
}

/** Response of POST /api/v1/auth/verify-email. `verified` is true on a correct,
 *  unexpired, unused code (or when already verified). 400 + {verified:false} on a
 *  wrong/expired/used code. */
export interface VerifyEmailResponse {
  verified: boolean;
  already?: boolean;
}

/** Issue + email a fresh 6-digit verification code to the signed-in user. */
export const requestEmailVerification = () =>
  api.post<RequestEmailVerificationResponse>(
    '/api/v1/auth/request-email-verification',
  );

/** Verify the signed-in user's email with a submitted 6-digit code. */
export const verifyEmail = (code: string) =>
  api.post<VerifyEmailResponse>('/api/v1/auth/verify-email', { code });

export const listCompetitions = (tag?: string) =>
  api.get<Competition[]>('/api/v1/competitions', tag ? { params: { tag } } : undefined);

// ── Total winnable prize value (v4-7-2) ────────────────────────────────────────
/** One currency's slice of the live winnable-prize-value total. */
export interface PrizeValueByCurrency {
  currency: string;
  total_prize_value_cents: number;
  cash_redeemable_value_cents: number;
  live_competition_count: number;
}

/** "What your audience could win right now" — the total factual prize value across
 *  competitions currently live/open for entry. Headline figures describe the DOMINANT
 *  currency (most live comps); never summed across currencies. `by_currency` carries
 *  the full per-currency breakdown; `currency_count > 1` means prizes span currencies. */
export interface TotalPrizeValue {
  total_prize_value_cents: number;
  currency: string;
  live_competition_count: number;
  cash_redeemable_value_cents: number;
  currency_count: number;
  by_currency: PrizeValueByCurrency[];
}

/** Public — total winnable prize value across live competitions (partner toolkit stat). */
export const getTotalPrizeValue = () =>
  api.get<TotalPrizeValue>('/api/v1/competitions/total-prize-value');

export const getCompetition = (id: string) =>
  api.get<Competition>(`/api/v1/competitions/${id}`);

// Friendly /win/<slug> URL → resolve the competition by its slug.
export const getCompetitionBySlug = (slug: string) =>
  api.get<Competition>(`/api/v1/competitions/by-slug/${encodeURIComponent(slug)}`);

export interface EntryPurchaseRequest {
  success_url: string;
  cancel_url: string;
  redeem_rands?: string;
  /** v4-epic-4: entrant's skill answer, submitted at entry time. Shape depends on
   *  the competition's skill_type (e.g. closest_guess → { value: number }). Omit /
   *  null for "none" (random) competitions. The entrant UI populates this. */
  skill_answer?: Record<string, unknown> | null;
  /** skill-tiebreak-1: the server-signed challenge token from getSkillChallenge(),
   *  fetched when the skill question is shown. REQUIRED only when the competition's
   *  tiebreak_mode is "reaction_speed" (so the answer can be timed server-side);
   *  omit otherwise. */
  skill_challenge_token?: string | null;
  /** v4-epic-5: when true, use the caller's subscriber free entry instead of paying.
   *  Only offer this when getSubscriberFreeAvailability() returns a non-empty list.
   *  Still skill-gated (skill_answer required when the comp has a skill). The backend
   *  creates a confirmed, zero-charge entry — checkout_url comes back null. */
  use_subscriber_free_entry?: boolean;
  /** corp-1-3: on a SPONSORED competition, the entrant's explicit consent to share
   *  their details with the sponsor. Default false (no consent). Ignored for
   *  non-sponsored competitions. When true the backend records a PII-free
   *  lead_opt_in signal + forwards it to the sponsor's service. */
  opt_in_to_sponsor?: boolean;
}

export interface EntryPurchaseResponse {
  entry_id: string;
  /** null when no gateway redirect is needed (wallet-covered or subscriber-free). */
  checkout_url: string | null;
  payment_reference: string;
}

/** v4-epic-5: the owner's other services for which the caller is an active PAID
 *  subscriber WITH an unspent free entry right now. Empty when the feature flag is
 *  off, the user has no qualifying subscriptions, or every free entry is in play. */
export interface SubscriberFreeAvailabilityResponse {
  available_products: string[];
}

export interface EntryResponse {
  id: string;
  competition_id: string;
  competition_title: string;
  status: string;
  payment_reference: string | null;
  is_boost: boolean;
  created_at: string;
  // v4-4-12: per-entry skill state. A skill allocated AFTER purchase leaves an entry
  // needing an answer — needs completion when skill_type !== "none" && !has_skill_answer
  // && status === "confirmed".
  skill_type?: SkillType;
  has_skill_answer?: boolean;
  skill_submission_deadline?: string | null;
}

/** Result of completing the skill on an existing entry (v4-4-12). */
export interface SkillAnswerStateResponse {
  entry_id: string;
  skill_answered: boolean;
  skill_score: number | null;
}

/** skill-tiebreak-1: a fresh server-signed skill challenge. The `token` is opaque
 *  (never parse it) and must be sent back on entry/boost/complete-skill so the server
 *  can measure the reaction time. Only needed for reaction_speed competitions. */
export interface SkillChallengeResponse {
  token: string;
  served_at_ms: number;
  tiebreak_mode: TiebreakMode;
  /** The entrant-safe skill question (answer key stripped). For a reaction_speed comp
   *  this is the ONLY source of the question — it is NOT on public competition detail —
   *  so the UI must render the game from HERE, and fetching it marks first sight. */
  skill?: Record<string, unknown> | null;
}

/** skill-tiebreak-1: serve the signed challenge AND reveal the question for a
 *  reaction_speed competition; fetching it stamps first sight and times the answer. */
export const getSkillChallenge = (competitionId: string) =>
  api.post<SkillChallengeResponse>(
    `/api/v1/competitions/${competitionId}/skill-challenge`,
    {},
  );

export const purchaseEntry = (competitionId: string, payload: EntryPurchaseRequest) =>
  api.post<EntryPurchaseResponse>(
    `/api/v1/competitions/${competitionId}/entries`,
    payload,
  );

export const getMyEntries = () => api.get<EntryResponse[]>('/api/v1/me/entries');

/** v4-epic-5 — which of the owner's other services the caller can use a free entry
 *  from right now (active paid subscriber + unspent rolling free entry). Empty list
 *  when nothing is available; the UI offers the perk only when it's non-empty. */
export const getSubscriberFreeAvailability = () =>
  api.get<SubscriberFreeAvailabilityResponse>(
    '/api/v1/me/subscriber-free-availability',
  );

/** v4-4-12 — play the skill on an EXISTING already-paid entry to validate it. */
export const submitSkillAnswer = (
  competitionId: string,
  entryId: string,
  skillAnswer: Record<string, unknown>,
  skillChallengeToken?: string | null,
) =>
  api.post<SkillAnswerStateResponse>(
    `/api/v1/competitions/${competitionId}/entries/${entryId}/skill-answer`,
    { skill_answer: skillAnswer, skill_challenge_token: skillChallengeToken ?? null },
  );

// ── Share-to-Enter free entry (v4-epic-6) ────────────────────────────────────
/** Per-competition, PLATFORM-DISTINCT share credit ledger (GET /share-status).
 *
 *  For competition X a user earns ONE credit per DISTINCT public PLATFORM they've
 *  verifiably shared X on (either an auto-tracked link visit OR an admin-approved
 *  screenshot). `required` distinct platforms → ONE complimentary (R0) entry into X.
 *
 *  - `share_link_base` is the caller's tracking link for X; the FE appends
 *    `?p={platform}` to build each platform-specific share URL.
 *  - `allowed_platforms` are the only public-verifiable platforms that count.
 *  - `credited_platforms` are the platforms already credited for THIS competition.
 *  - `can_claim` is true when there's a spare earned credit AND the comp is open AND
 *    the caller is under max_entries_per_user. */
export interface ShareStatusResponse {
  share_link_base: string;
  allowed_platforms: string[];
  credited_platforms: string[];
  credits: number;
  required: number;
  earned: number;
  claimed: number;
  available: number;
  can_claim: boolean;
}

/** Status of a manual (untrackable) screenshot claim awaiting admin verification. */
export type ShareScreenshotStatus = 'pending' | 'approved' | 'rejected';

/** One screenshot claim the caller submitted as proof of an untrackable share.
 *  An approved claim becomes a platform credit for (user, competition, platform). */
export interface ShareScreenshotClaim {
  id: string;
  competition_id: string | null;
  platform: string;
  screenshot_url: string;
  public_url: string | null;
  status: ShareScreenshotStatus;
  reject_reason: string | null;
  created_at: string;
}

/** Result of claiming the complimentary (R0) share entry. The granted entry is
 *  skill-gated and carries no skill answer — the user completes it via the
 *  existing v4-4-12 complete-skill flow (has_skill_answer=false on /me/entries). */
export interface ClaimShareEntryResponse {
  entry_id: string;
  free_entry_source: string;
  status: string;
}

/** The caller's per-competition, platform-distinct share-credit ledger. */
export const getShareStatus = (competitionId: string) =>
  api.get<ShareStatusResponse>(
    `/api/v1/competitions/${competitionId}/share-status`,
  );

/** Upload proof of an UNTRACKABLE share (multipart): the platform, the PUBLIC post
 *  URL (so an admin can open + verify it), and a JPEG/PNG screenshot (≤10MB). Creates
 *  a PENDING claim; on admin approval it credits that platform for this competition. */
export const submitShareScreenshot = (
  competitionId: string,
  payload: { platform: string; public_url: string; screenshot: File },
) => {
  const form = new FormData();
  form.append('platform', payload.platform);
  form.append('public_url', payload.public_url);
  form.append('screenshot', payload.screenshot);
  return api.post<ShareScreenshotClaim>(
    `/api/v1/competitions/${competitionId}/share-screenshot`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
};

/** The caller's screenshot claims (all competitions), newest first. */
export const listMyShareScreenshots = () =>
  api.get<ShareScreenshotClaim[]>('/api/v1/me/share-screenshots');

/** Redeem ONE earned credit into this competition (R0 confirmed entry).
 *  400 when not eligible. Idempotent per earned credit server-side. */
export const claimShareEntry = (competitionId: string) =>
  api.post<ClaimShareEntryResponse>(
    `/api/v1/competitions/${competitionId}/claim-share-entry`,
  );

// ── Admin: share-screenshot review queue (v4-epic-6) ──────────────────────────
/** One screenshot claim in the admin review queue. The admin opens `public_url`,
 *  confirms a publicly-visible post, and approves (crediting the platform) or
 *  rejects with a reason. */
export interface AdminShareScreenshot {
  id: string;
  user_id: string;
  competition_id: string;
  platform: string;
  screenshot_url: string;
  public_url: string;
  status: ShareScreenshotStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  reject_reason: string | null;
  created_at: string;
}

/** List screenshot claims for review, optionally filtered by status, newest first. */
export const adminListShareScreenshots = (adminEmail: string, status?: string) =>
  api.get<AdminShareScreenshot[]>('/api/v1/admin/share-screenshots', {
    headers: adminHeaders(adminEmail),
    params: status ? { status } : undefined,
  });

/** Approve a claim → credits (user, competition, platform). The admin may optionally
 *  re-target the competition/platform if the user mis-picked. Idempotent. */
export const adminApproveShareScreenshot = (
  adminEmail: string,
  id: string,
  corrections?: { competition_id?: string; platform?: string },
) =>
  api.post<AdminShareScreenshot>(
    `/api/v1/admin/share-screenshots/${id}/approve`,
    corrections ?? {},
    { headers: adminHeaders(adminEmail) },
  );

/** Reject a claim (e.g. private/unviewable post) with a required reason. */
export const adminRejectShareScreenshot = (
  adminEmail: string,
  id: string,
  reason: string,
) =>
  api.post<AdminShareScreenshot>(
    `/api/v1/admin/share-screenshots/${id}/reject`,
    { reason },
    { headers: adminHeaders(adminEmail) },
  );

export interface BoostPurchaseResponse {
  entry_id: string;
  checkout_url: string;
  payment_reference: string;
  boost_slots_remaining: number;
}

export interface BoostPurchaseRequest {
  success_url: string;
  cancel_url: string;
  redeem_rands?: string;
  /** hotfix-1-154: each boost on a skill competition is its OWN fresh play and
   *  must carry its own skill answer. Shape depends on the competition's skill_type.
   *  Omit / null for "none" (random) competitions. The entrant UI populates this. */
  skill_answer?: Record<string, unknown> | null;
  /** skill-tiebreak-1: server-signed challenge token (see EntryPurchaseRequest).
   *  Each boost is its own timed play — REQUIRED only for reaction_speed comps. */
  skill_challenge_token?: string | null;
}

export const purchaseBoost = (competitionId: string, payload: BoostPurchaseRequest) =>
  api.post<BoostPurchaseResponse>(
    `/api/v1/competitions/${competitionId}/boost`,
    payload,
  );

export interface WinnerResponse {
  winner_id: string;
  competition_id: string;
  user_id: string;
  announced_at: string;
  fulfilment_status: string;
}

export const getCompetitionWinner = (competitionId: string) =>
  api.get<WinnerResponse>(`/api/v1/competitions/${competitionId}/winner`);

export interface WinnerHistoryItem {
  competition_id: string;
  competition_title: string;
  prize_description: string;
  draw_date: string;
  announced_at: string;
  winner_display_name: string;
  fulfilment_status: string;
}

export interface WinnerHistoryResponse {
  items: WinnerHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

export const listWinners = (limit = 20, offset = 0) =>
  api.get<WinnerHistoryResponse>('/api/v1/winners', { params: { limit, offset } });

// ── Admin types ──────────────────────────────────────────────────────────────
export interface AdminCompetition {
  id: string;
  title: string;
  prize_description: string;
  prize_media_urls: string[];
  /** mktg-student-village: themed collection/tile tags (e.g. ["student-village"]). */
  tags?: string[];
  terms_markdown: string;
  entry_fee_cents: number;
  currency: string;
  /** hotfix-1-56: prize monetary value + its own ISO-4217 currency. Null on legacy
   *  rows the backfill couldn't parse. Field names match the backend exactly. */
  prize_value_cents: number | null;
  prize_value_currency_code: string | null;
  max_entries_per_user: number;
  /** null when not yet configured (pre_launch/new competitions) */
  participant_threshold: number | null;
  draw_date: string | null;
  status: string;
  /** null when no entries have been recorded yet */
  unique_participant_count: number | null;
  created_at: string;
  /** v4-3-1: estimated GROSS revenue at target = entry_fee × participant_threshold. */
  estimated_revenue_cents?: number;
  // ── Skill mechanic (v4-epic-4) — admin view ──
  /** Defaults to "none". */
  skill_type?: SkillType;
  /** FULL skill config including the answer key (admin-only response). null for "none". */
  skill_config?: Record<string, unknown> | null;
  /** ISO datetime — set when the skill answers were allocated/locked. null before. */
  skill_allocated_at?: string | null;
  /** ISO datetime — deadline by which entrants must submit their skill answer. */
  skill_submission_deadline?: string | null;
  /** skill-tiebreak-1: how an equal-top-score tie is broken (default
   *  "submission_order"). Only meaningful for skill competitions. */
  tiebreak_mode?: TiebreakMode;
}

export interface AdminCompetitionVideoAsset extends CompetitionVideoAssetPublic {
  id: string;
  competition_id: string;
  review_status: 'draft' | 'approved' | 'rejected';
  publication_state: 'unpromoted' | 'promoted';
  status: 'draft' | 'approved' | 'rejected' | 'archived';
  size_bytes?: number | null;
  poster_mime_type?: string | null;
  poster_size_bytes?: number | null;
  video_sha256?: string | null;
  poster_sha256?: string | null;
  source_job_id?: string | null;
  manifest_version?: string | null;
  manifest_id?: string | null;
  canonical_url?: string | null;
  requested_provider?: string | null;
  provider_job_id?: string | null;
  model?: string | null;
  project?: string | null;
  executing_principal?: string | null;
  created_by: string;
  approved_by?: string | null;
  rejected_by?: string | null;
  promoted_by?: string | null;
  created_at: string;
  approved_at?: string | null;
  rejected_at?: string | null;
  promoted_at?: string | null;
  archived_at?: string | null;
}

// v4-3-1: admin profit/revenue estimation
export interface ProfitSummaryItem {
  id: string;
  title: string;
  status: string;
  currency_code: string;
  entry_fee_cents: number;
  participant_threshold: number;
  unique_participant_count: number;
  estimated_revenue_cents: number;
  realised_revenue_cents: number;
}
export interface ProfitSummary {
  open_competition_count: number;
  totals_by_currency: Record<string, number>;
  realised_by_currency: Record<string, number>;
  competitions: ProfitSummaryItem[];
}

export interface AdminCompetitionCreate {
  title: string;
  prize_description: string;
  prize_media_urls?: string[];
  terms_markdown: string;
  entry_fee_cents: number;
  currency: string;
  prize_value_cents: number; // hotfix-1-56: required at creation
  prize_value_currency_code?: string; // defaults to the competition currency
  max_entries_per_user: number;
  participant_threshold: number;
  draw_date?: string;
  // ── Skill mechanic (v4-epic-4) ──
  /** Defaults to "none" server-side when omitted. */
  skill_type?: SkillType;
  /** Type-specific config (e.g. ClosestGuessConfig). Required when skill_type !== "none". */
  skill_config?: Record<string, unknown>;
  /** skill-tiebreak-1: tie-break mode chosen at campaign time. Defaults to
   *  "submission_order" server-side when omitted. */
  tiebreak_mode?: TiebreakMode;
}

export interface CompetitionImportResult {
  title: string;
  prize_description: string;
  prize_media_urls: string[];
  source_url: string;
}

export interface AdminCompetitionUpdate {
  title?: string;
  prize_description?: string;
  terms_markdown?: string;
  entry_fee_cents?: number;
  currency?: string;
  prize_value_cents?: number;
  prize_value_currency_code?: string;
  max_entries_per_user?: number;
  participant_threshold?: number;
  draw_date?: string;
  // ── Skill mechanic (v4-epic-4) ──
  skill_type?: SkillType;
  skill_config?: Record<string, unknown>;
  /** skill-tiebreak-1: change the tie-break mode (editable pre-draw). */
  tiebreak_mode?: TiebreakMode;
  /** ISO datetime — required by the backend when allocating a skill on a
   *  competition that already has confirmed entries. */
  skill_submission_deadline?: string;
  /** When changing the skill of a competition that already has answers, the
   *  backend returns 400 unless force=true is passed to confirm the override. */
  force?: boolean;
}

export interface CompetitionStats {
  unique_participant_count: number;
  total_entry_count: number;
  total_revenue_cents: number;
  top_10_entrants: Record<string, unknown>[];
}

// ── Admin helpers ─────────────────────────────────────────────────────────────
function adminHeaders(email: string) {
  return { 'X-Admin-Email': email };
}

// ── Admin competition CRUD ────────────────────────────────────────────────────
export const adminImportUrl = (url: string, email: string) =>
  api.post<CompetitionImportResult>(
    '/api/v1/admin/competitions/import-url',
    { url },
    { headers: adminHeaders(email) },
  );

export const adminListCompetitions = (email: string, tag?: string) =>
  api.get<AdminCompetition[]>('/api/v1/admin/competitions', {
    headers: adminHeaders(email),
    ...(tag ? { params: { tag } } : {}),
  });

/** v4-3-1: estimated gross revenue per open competition + aggregate by currency. */
export const adminGetProfitSummary = (email: string) =>
  api.get<ProfitSummary>('/api/v1/admin/competitions/profit-summary', { headers: adminHeaders(email) });

export const adminGetCompetition = (id: string, email: string) =>
  api.get<AdminCompetition>(`/api/v1/admin/competitions/${id}`, { headers: adminHeaders(email) });

export const adminCreateCompetition = (data: AdminCompetitionCreate, email: string) =>
  api.post<AdminCompetition>('/api/v1/admin/competitions', data, { headers: adminHeaders(email) });

export const adminUpdateCompetition = (id: string, data: AdminCompetitionUpdate, email: string) =>
  api.patch<AdminCompetition>(`/api/v1/admin/competitions/${id}`, data, { headers: adminHeaders(email) });

/**
 * Transition a competition to a new status.
 * NOTE: the request body field is `status` (backend StatusUpdate model), NOT `new_status`.
 * The `newStatus` parameter name is kept camelCase per JS convention; it maps to the
 * `status` key on the wire.
 */
export const adminUpdateStatus = (
  id: string,
  newStatus: string,
  email: string,
  extra?: Record<string, unknown>,
) =>
  api.patch<AdminCompetition>(
    `/api/v1/admin/competitions/${id}/status`,
    // Backend StatusUpdate model field is `status`, not `new_status`.
    { status: newStatus, ...extra },
    { headers: adminHeaders(email) },
  );

export const adminDeleteCompetition = (id: string, email: string) =>
  api.delete(`/api/v1/admin/competitions/${id}`, { headers: adminHeaders(email) });

export const adminGetStats = (id: string, email: string) =>
  api.get<CompetitionStats>(`/api/v1/admin/competitions/${id}/stats`, { headers: adminHeaders(email) });

export const adminTriggerDraw = (id: string, email: string) =>
  api.post<AdminCompetition>(`/api/v1/admin/competitions/${id}/draw`, {}, { headers: adminHeaders(email) });

export const adminUpdateFulfilment = (
  id: string,
  data: { fulfilment_status: string },
  email: string,
) =>
  api.patch<AdminCompetition>(`/api/v1/admin/competitions/${id}/fulfilment`, data, { headers: adminHeaders(email) });

// ── Caption judging + skill readiness (v4-4-7 / v4-4-4) ────────────────────────

/** One caption_this entry awaiting (or carrying) a judge's score. skill_score is
 *  null until the entry has been judged. */
export interface JudgingEntry {
  entry_id: string;
  user_id: string;
  text: string | null;
  skill_score: number | null;
}

/** Result of scoring one entry — the weighted skill_score the backend computed. */
export interface JudgeResult {
  entry_id: string;
  skill_score: number;
}

/** v4-4-4: operator eligibility view before drawing a skill competition.
 *  unanswered (past deadline) entries are refunded to the wallet on draw. */
export interface SkillReadiness {
  confirmed: number;
  answered: number;
  unanswered: number;
  deadline: string | null;
}

/** List a caption_this competition's confirmed entries for judging.
 *  400 if the competition is not a caption_this competition. */
export const getJudgingEntries = (email: string, compId: string) =>
  api.get<JudgingEntry[]>(
    `/api/v1/admin/competitions/${compId}/judging`,
    { headers: adminHeaders(email) },
  );

/** Score one caption entry. `scores` is keyed by the published criterion label
 *  (each value 0..100). Returns the resulting weighted skill_score. */
export const judgeEntry = (
  email: string,
  compId: string,
  entryId: string,
  scores: Record<string, number>,
) =>
  api.post<JudgeResult>(
    `/api/v1/admin/competitions/${compId}/entries/${entryId}/judge`,
    { scores },
    { headers: adminHeaders(email) },
  );

/** Allocate a member (by user_id) as the tie-break judge for this competition. */
export const allocateJudge = (email: string, compId: string, userId: string) =>
  api.post<{ ok: boolean }>(
    `/api/v1/admin/competitions/${compId}/allocate-judge`,
    { user_id: userId },
    { headers: adminHeaders(email) },
  );

/** Eligibility snapshot (confirmed / answered / unanswered + deadline) before draw. */
export const getSkillReadiness = (email: string, compId: string) =>
  api.get<SkillReadiness>(
    `/api/v1/admin/competitions/${compId}/skill-readiness`,
    { headers: adminHeaders(email) },
  );

// ── Skill / question rotation helper (v4-4-9) ─────────────────────────────────
/** One recent competition in the rotation hint. `question` is its skill prompt
 *  (question / prompt / instructions), or null when it has none. */
export interface RotationRecentItem {
  id: string;
  title: string;
  skill_type: SkillType;
  question: string | null;
}

/** Non-blocking advice: alternate skills, don't repeat the same question. */
export interface SkillRotationHint {
  recent: RotationRecentItem[];
  /** The most recent non-"none" competition uses this same skill_type. */
  repeats_type: boolean;
  /** A recent competition has an identical (case-insensitive, trimmed) question. */
  repeats_question: boolean;
}

/** v4-4-9: advisory rotation hint for the competition the operator is composing.
 *  Purely informational — it never blocks create/update. */
export const getSkillRotationHint = (
  email: string,
  skillType: SkillType,
  question?: string,
) => {
  const params = new URLSearchParams({ skill_type: skillType });
  if (question && question.trim()) params.set('question', question);
  return api.get<SkillRotationHint>(
    `/api/v1/admin/competitions/skill-rotation-hint?${params.toString()}`,
    { headers: adminHeaders(email) },
  );
};

// ── Flyer management ─────────────────────────────────────────────────────────

export interface FlyerResponse {
  id: string;
  competition_id: string;
  image_url: string | null;
  thumbnail_url: string | null;
  status: 'draft' | 'generating' | 'approved' | 'archived';
  generation_method: 'uploaded' | 'ai_generated' | 'animated_gif';
  generation_prompt: string | null;
  created_by: string;
  approved_by: string | null;
  created_at: string;
  approved_at: string | null;
  sort_order: number;
}

export interface FlyerCreatePayload {
  method: 'ai_generated' | 'uploaded' | 'animated_gif';
  prompt?: string;
  image_base64?: string;
}

export interface FlyerUpdatePayload {
  status?: 'approved' | 'archived' | 'draft';
  sort_order?: number;
}

export const adminListFlyers = (competitionId: string, adminEmail: string) =>
  api.get<FlyerResponse[]>(
    `/api/v1/admin/competitions/${competitionId}/flyers`,
    { headers: adminHeaders(adminEmail) },
  );

export const adminCreateFlyer = (
  competitionId: string,
  payload: FlyerCreatePayload,
  adminEmail: string,
) =>
  api.post<{ flyer_id: string; status: string }>(
    `/api/v1/admin/competitions/${competitionId}/flyers`,
    payload,
    { headers: adminHeaders(adminEmail) },
  );

export const adminUpdateFlyer = (
  competitionId: string,
  flyerId: string,
  payload: FlyerUpdatePayload,
  adminEmail: string,
) =>
  api.patch<FlyerResponse>(
    `/api/v1/admin/competitions/${competitionId}/flyers/${flyerId}`,
    payload,
    { headers: adminHeaders(adminEmail) },
  );

export const adminDeleteFlyer = (
  competitionId: string,
  flyerId: string,
  adminEmail: string,
) =>
  api.delete(
    `/api/v1/admin/competitions/${competitionId}/flyers/${flyerId}`,
    { headers: adminHeaders(adminEmail) },
  );

// ── Prize media management ────────────────────────────────────────────────────

export const adminUploadMedia = (id: string, imageBase64: string, adminEmail: string) =>
  api.post<AdminCompetition>(
    `/api/v1/admin/competitions/${id}/media`,
    { image_base64: imageBase64 },
    { headers: adminHeaders(adminEmail) },
  );

export const adminDeleteMedia = (id: string, url: string, adminEmail: string) =>
  api.delete<AdminCompetition>(
    `/api/v1/admin/competitions/${id}/media`,
    { data: { url }, headers: adminHeaders(adminEmail) },
  );

// Generate a clean AI prize image (Imagen 3) and append it to prize_media_urls.
// Generation takes ~30-60s, so allow a long client timeout.
export const adminGeneratePrizeImage = (id: string, adminEmail: string) =>
  api.post<AdminCompetition>(
    `/api/v1/admin/competitions/${id}/generate-prize-image`,
    {},
    { headers: adminHeaders(adminEmail), timeout: 120000 },
  );

// Promote a prize image to the hero slot (moves it to prize_media_urls[0]).
export const adminSetHeroMedia = (id: string, url: string, adminEmail: string) =>
  api.post<AdminCompetition>(
    `/api/v1/admin/competitions/${id}/media/hero`,
    { url },
    { headers: adminHeaders(adminEmail) },
  );

export interface AdminCompetitionVideoAssetCreate {
  provider: string;
  video_base64?: string;
  video_url?: string;
  poster_base64?: string;
  poster_url?: string;
  duration_seconds?: number;
  aspect_ratio?: string;
}

export interface AdminCompetitionVideoAssetUpdate {
  review_status: 'draft' | 'approved' | 'rejected';
}

export const adminListCompetitionVideoAssets = (
  competitionId: string,
  adminEmail: string,
) =>
  api.get<AdminCompetitionVideoAsset[]>(
    `/api/v1/admin/competitions/${competitionId}/video-assets`,
    { headers: adminHeaders(adminEmail) },
  );

export const adminCreateCompetitionVideoAsset = (
  competitionId: string,
  payload: AdminCompetitionVideoAssetCreate,
  adminEmail: string,
) =>
  api.post<AdminCompetitionVideoAsset>(
    `/api/v1/admin/competitions/${competitionId}/video-assets`,
    payload,
    { headers: adminHeaders(adminEmail), timeout: 120000 },
  );

export const adminUpdateCompetitionVideoAsset = (
  competitionId: string,
  assetId: string,
  payload: AdminCompetitionVideoAssetUpdate,
  adminEmail: string,
) =>
  api.patch<AdminCompetitionVideoAsset>(
    `/api/v1/admin/competitions/${competitionId}/video-assets/${assetId}`,
    payload,
    { headers: adminHeaders(adminEmail) },
  );

export const adminPromoteCompetitionVideoAsset = (
  competitionId: string,
  assetId: string,
  adminEmail: string,
) =>
  api.post<AdminCompetitionVideoAsset>(
    `/api/v1/admin/competitions/${competitionId}/video-assets/${assetId}/promote`,
    {},
    { headers: adminHeaders(adminEmail) },
  );

export const adminRollbackCompetitionVideoAsset = (
  competitionId: string,
  assetId: string,
  adminEmail: string,
) =>
  api.post<AdminCompetitionVideoAsset>(
    `/api/v1/admin/competitions/${competitionId}/video-assets/${assetId}/rollback`,
    {},
    { headers: adminHeaders(adminEmail) },
  );

// ── Public stats ─────────────────────────────────────────────────────────────

export interface LoyaltyStats {
  total_members: number;
  total_referrals: number;
  total_issued_rands: number;
  total_redeemed_rands: number;
  reward_per_referral_zar: number;
  reward_per_referral_usd: number;
  max_potential_zar: number;
  max_potential_usd: number;
  total_issued_zar: number;
  total_issued_usd: number;
}

export const getLoyaltyStats = () => api.get<LoyaltyStats>('/api/v1/stats/loyalty');

// ── Affiliate products (hotfix-1-161) ──────────────────────────────────────────
/** One "More from My Investments" affiliate card. Copy is fixed (attorney-approved);
 *  prices are fetched live from the payments service. `available: false` (no real
 *  price set, or the lookup failed) → render "Coming soon" instead of a price.
 *  Shape mirrors the backend AffiliateProduct model exactly. */
export interface AffiliateProduct {
  slug: string;
  name: string;
  blurb: string;
  url: string;
  /** Lowest monthly tier in cents; null when unavailable ("Coming soon"). */
  from_price_cents: number | null;
  /** ISO-4217 currency for from_price_cents; null when unavailable. */
  currency: string | null;
  available: boolean;
}

/** Public — the three affiliate cards with live "from" prices for the landing page. */
export const getAffiliateProducts = () =>
  api.get<AffiliateProduct[]>('/api/v1/affiliate/products');

// ── User loyalty wallet ───────────────────────────────────────────────────────

export interface LoyaltyTransaction {
  id: string;
  type: string;
  amount: string;
  currency: string;
  competition_id?: string | null;
  redeemed_on_competition_id?: string | null;
  created_at: string;
}

export interface LoyaltyResponse {
  balance: string;
  reserved: string;              // locked by pending/approved withdrawals (v4-1-2)
  available: string;             // balance - reserved: spendable in-app AND withdrawable
  wallet_currency: string;       // "ZAR" (SA users) or "USD" (international)
  unlocked: string;
  transactions: LoyaltyTransaction[];
  successful_referrals: number;  // renamed from referral_count — matches backend field
  pending_referrals: number;
  reward_per_referral: string;
  cap: number;                   // user's referral reward cap
  potential: string;
  unrealized: string;
}

// URL is /api/v1/users/me/loyalty — matches backend loyalty router prefix /users/me
export const getLoyalty = () => api.get<LoyaltyResponse>('/api/v1/users/me/loyalty');

// ── Withdrawals (member cash-out, v4-1-2/1-4) ──────────────────────────────────

export interface BankAccountInput {
  account_holder_name: string;
  bank_name: string;
  account_number: string;
  branch_code: string;
  account_type: string;
}

export interface BankAccountOut {
  account_holder_name: string;
  bank_name: string;
  account_number_masked: string;   // "••••1234" — full number never returned
  branch_code: string;
  account_type: string;
  updated_at?: string | null;
}

export interface WithdrawalOut {
  id: string;
  amount: string;
  currency: string;
  status: string;                  // pending | approved | paid | rejected
  reject_reason?: string | null;
  requested_at?: string | null;
  decided_at?: string | null;
  paid_at?: string | null;
}

export const getBankAccount = () => api.get<BankAccountOut>('/api/v1/users/me/bank-account');
export const saveBankAccount = (b: BankAccountInput) =>
  api.put<BankAccountOut>('/api/v1/users/me/bank-account', b);
export const listWithdrawals = () =>
  api.get<WithdrawalOut[]>('/api/v1/users/me/withdrawals');
export const requestWithdrawal = (amount: string) =>
  api.post<WithdrawalOut>('/api/v1/users/me/withdrawals', { amount });
export const cancelWithdrawal = (id: string) =>
  api.delete<WithdrawalOut>(`/api/v1/users/me/withdrawals/${id}`);

// ── Admin withdrawal payout queue (v4-1-3/1-4) ─────────────────────────────────

export interface AdminBankSnapshot {
  account_holder_name: string;
  bank_name: string;
  account_number: string;
  branch_code: string;
  account_type: string;
}

export interface AdminWithdrawal {
  id: string;
  user_id: string;
  user_email: string;
  amount: string;
  currency: string;
  status: string;
  reject_reason?: string | null;
  requested_at?: string | null;
  decided_at?: string | null;
  decided_by?: string | null;
  paid_at?: string | null;
  bank: AdminBankSnapshot;
}

export const listAdminWithdrawals = (adminEmail: string, status?: string) =>
  api.get<AdminWithdrawal[]>('/api/v1/admin/withdrawals', {
    headers: { 'X-Admin-Email': adminEmail },
    params: status ? { status } : undefined,
  });
export const approveWithdrawal = (adminEmail: string, id: string) =>
  api.post<AdminWithdrawal>(`/api/v1/admin/withdrawals/${id}/approve`, null, {
    headers: { 'X-Admin-Email': adminEmail },
  });
export const markWithdrawalPaid = (adminEmail: string, id: string) =>
  api.post<AdminWithdrawal>(`/api/v1/admin/withdrawals/${id}/paid`, null, {
    headers: { 'X-Admin-Email': adminEmail },
  });
export const rejectWithdrawal = (adminEmail: string, id: string, reason: string) =>
  api.post<AdminWithdrawal>(`/api/v1/admin/withdrawals/${id}/reject`, { reason }, {
    headers: { 'X-Admin-Email': adminEmail },
  });

// ── Business Partner program (v4-2-3) ──────────────────────────────────────────

export type ContactMethod = 'whatsapp' | 'call' | 'email';

export interface PartnerStatus {
  status: string;                  // none | pending | approved | rejected
  qualifying_clients: number;
  milestone_target: number;
  bonus_amount: string;
  bonus_paid: boolean;
  reject_reason?: string | null;
  contact_method?: ContactMethod | null;
  contact_detail?: string | null;
  applied_at?: string | null;
  decided_at?: string | null;
  // ── Influencer readiness training/quiz state ──
  /** Best quiz score the partner has achieved (0–100). */
  quiz_best_pct?: number;
  /** How many times the partner has attempted the readiness quiz. */
  quiz_attempts?: number;
  /** ISO datetime of the first passing attempt; null if never passed. */
  quiz_passed_at?: string | null;
  /** True once the partner has passed the readiness quiz. */
  qualified?: boolean;
}

export interface PartnerApplication {
  contact_method: ContactMethod;
  contact_detail: string;
}

export interface AdminPartner {
  id: string;
  user_id: string;
  user_email: string;
  status: string;
  qualifying_clients: number;
  bonus_paid: boolean;
  reject_reason?: string | null;
  notes?: string | null;
  contact_method?: ContactMethod | null;
  contact_detail?: string | null;
  applied_at?: string | null;
  decided_at?: string | null;
  decided_by?: string | null;
}

export const getPartnerStatus = () => api.get<PartnerStatus>('/api/v1/users/me/partner');
export const applyForPartner = (application: PartnerApplication) =>
  api.post<PartnerStatus>('/api/v1/users/me/partner-application', application);

// ── Referred customers (v4-7-1) ────────────────────────────────────────────────
/** One referred customer, POPIA-masked (no raw email/phone is ever returned).
 *  `entry_kind` classifies their confirmed entries; `registered_not_entered`
 *  flags the nudge cohort (registered but has not entered a competition yet). */
export interface ReferredClient {
  initials: string;
  masked_email: string;
  registered_at: string;
  referral_status: 'registered' | 'rewarded';
  has_entered: boolean;
  entry_kind: 'paid' | 'free' | null;
  first_competition_id: string | null;
  first_competition_title: string | null;
  registered_not_entered: boolean;
}

export interface ReferredClientsPage {
  items: ReferredClient[];
  total: number;
  rewarded: number;
  registered_not_entered: number;
  limit: number;
  offset: number;
}

/** Approved partner's referred customers + entry status (cookie-auth). */
export const getPartnerClients = (limit = 50, offset = 0) =>
  api.get<ReferredClientsPage>('/api/v1/users/me/partner/clients', {
    params: { limit, offset },
  });

// ── Influencer training hub + readiness quiz ───────────────────────────────────
/** Optional rich media for a training module. All fields are null for now — the
 *  hub renders text-first and only shows a media element when its URL is present. */
export interface TrainingMedia {
  infographic_url: string | null;
  video_url: string | null;
  audio_url: string | null;
  pdf_url: string | null;
}

/** One heading + body block within a training module. */
export interface TrainingSection {
  heading: string;
  body: string;
}

/** A single self-study module in the partner training hub. */
export interface TrainingModule {
  id: string;
  order: number;
  title: string;
  summary: string;
  sections: TrainingSection[];
  media: TrainingMedia;
}

export interface TrainingResponse {
  modules: TrainingModule[];
}

/** A quiz question. `options` are the answer choices; the entrant submits the
 *  selected option's index. The correct index is never sent in this payload. */
export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
}

export interface QuizResponse {
  questions: QuizQuestion[];
  pass_pct: number;
}

/** Per-question result returned after submitting — drives the learn-from-it review. */
export interface QuizQuestionResult {
  id: string;
  correct: boolean;
  correct_index: number;
  explanation: string;
}

export interface QuizSubmissionResult {
  score_pct: number;
  correct_count: number;
  total: number;
  passed: boolean;
  pass_pct: number;
  results: QuizQuestionResult[];
  /** True once the user has met the qualification bar (e.g. a prior passing attempt). */
  qualified: boolean;
}

/** The training modules for the signed-in partner (cookie-auth, same as /users/me). */
export const getPartnerTraining = () =>
  api.get<TrainingResponse>('/api/v1/users/me/training');

/** The readiness quiz questions (no answer key). */
export const getPartnerQuiz = () =>
  api.get<QuizResponse>('/api/v1/users/me/quiz');

/** Submit quiz answers, keyed by question id → selected option index. */
export const submitPartnerQuiz = (answers: Record<string, number>) =>
  api.post<QuizSubmissionResult>('/api/v1/users/me/quiz', { answers });

// ── Marketing toolkit (copy-paste swipe file) ──────────────────────────────────
/** A single sales hook with the persuasion principle it leans on. */
export interface ToolkitSalesHook {
  hook: string;
  principle: string;
}

/** A ready-to-send WhatsApp script — title + the message body. */
export interface ToolkitWhatsappScript {
  title: string;
  text: string;
}

/** A short-form video script — a title and an ordered list of beats. */
export interface ToolkitVideoScript {
  title: string;
  beats: string[];
}

/** A paid-ad creative — headline, body and call to action. */
export interface ToolkitAdCopy {
  headline: string;
  body: string;
  cta: string;
}

/**
 * The signed-in partner's copy-paste marketing toolkit. Every string already has
 * the partner's real referral link substituted in — there are no placeholders.
 */
export interface Toolkit {
  referral_url: string;
  /** Hooks grouped by audience, e.g. "Enter a prize (customers)". */
  sales_hooks: Record<string, ToolkitSalesHook[]>;
  hooks: string[];
  /** Captions grouped by platform, e.g. "WhatsApp / Status", "Instagram". */
  captions: Record<string, string[]>;
  whatsapp_scripts: ToolkitWhatsappScript[];
  video_scripts: ToolkitVideoScript[];
  ad_copy: ToolkitAdCopy[];
}

/** Get the signed-in partner's copy-paste marketing toolkit (cookie-auth). */
export const getToolkit = () => api.get<Toolkit>('/api/v1/users/me/toolkit');

// ── Partner referral link + QR ─────────────────────────────────────────────────
/** The partner's shareable link and a downloadable QR code (PNG data URI). */
export interface ReferralQR {
  referral_code: string;
  referral_url: string;
  /** A self-contained `data:image/png;base64,…` PNG — render directly in an <img>. */
  qr_data_uri: string;
}

/** Get the signed-in partner's referral link + QR code (cookie-auth).
 *  Pass a `competitionId` to get a QR DEEP-LINKED to that competition
 *  (/win/<slug>?ref=<code>) so a partner can promote a single prize (v4-7-3). */
export const getReferralQR = (competitionId?: string) =>
  api.get<ReferralQR>('/api/v1/users/me/referral-qr', {
    params: competitionId ? { competition_id: competitionId } : undefined,
  });

// ── Share-action attribution (v4-7-3) ──────────────────────────────────────────
/** The channels a partner can attribute a share action to. WhatsApp/Facebook/X
 *  have true web share-intents; Instagram/TikTok are copy-caption + open-app;
 *  copy_link is the plain "I copied my link" signal. Mirrors the backend's
 *  SHARE_PLATFORMS set exactly. */
export type SharePlatform =
  | 'whatsapp'
  | 'instagram'
  | 'tiktok'
  | 'facebook'
  | 'x'
  | 'copy_link';

/**
 * Record that the partner triggered a share action — lightweight, PII-free
 * attribution so we can later see which channel converts. FIRE-AND-FORGET: the
 * caller must NOT await/block the share on this; swallow any error.
 */
export const recordShareEvent = (
  platform: SharePlatform,
  competitionId?: string,
) =>
  api.post('/api/v1/users/me/share-events', {
    platform,
    ...(competitionId ? { competition_id: competitionId } : {}),
  });

export const listAdminPartners = (adminEmail: string, status?: string) =>
  api.get<AdminPartner[]>('/api/v1/admin/partners', {
    headers: { 'X-Admin-Email': adminEmail },
    params: status ? { status } : undefined,
  });
export const approvePartner = (adminEmail: string, id: string, notes?: string) =>
  api.post<AdminPartner>(`/api/v1/admin/partners/${id}/approve`, notes ? { notes } : {}, {
    headers: { 'X-Admin-Email': adminEmail },
  });
export const rejectPartner = (adminEmail: string, id: string, reason: string) =>
  api.post<AdminPartner>(`/api/v1/admin/partners/${id}/reject`, { reason }, {
    headers: { 'X-Admin-Email': adminEmail },
  });

// ── Admin loyalty stats ───────────────────────────────────────────────────────

export interface TopReferrer {
  user_id: string;
  email: string;
  referral_code: string;
  rewarded_referrals: number;
  total_earned_zar: string;
  total_earned_usd: string;
}

export interface AdminLoyaltyStats {
  total_referrals_pending: number;
  total_referrals_rewarded: number;
  total_rewards_issued_zar: string;
  total_rewards_issued_usd: string;
  total_redemptions_zar: string;
  total_redemptions_usd: string;
  outstanding_wallet_balance_zar: string;
  outstanding_wallet_balance_usd: string;
  top_referrers: TopReferrer[];
}

export const getAdminLoyaltyStats = (adminEmail: string) =>
  api.get<AdminLoyaltyStats>('/api/v1/admin/loyalty/stats', {
    headers: { 'X-Admin-Email': adminEmail },
  });

// ── Research & Proposals ──────────────────────────────────────────────────────

export interface ResearchTask {
  id: string;
  country_code: string;
  country_name: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  initiated_by: string;
  target_tier: string;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface CompetitionProposal {
  id: string;
  research_task_id: string;
  country_code: string;
  country_name: string;
  title: string;
  prize_description: string;
  prize_value_usd: number;
  entry_fee_usd: number;
  participant_threshold: number;
  max_entries_per_user: number;
  rationale: string | null;
  slogan_fit: string | null;
  platform_reachability: string | null;
  status: 'pending_review' | 'approved' | 'declined' | 'revision_requested';
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_competition_id: string | null;
  created_at: string;
}

export async function triggerResearch(
  countries: string[],
  email: string,
  target_tier: 'mass' | 'upper_mid' | 'premium' = 'upper_mid',
): Promise<{ task_ids: string[]; message: string }> {
  const res = await api.post<{ task_ids: string[]; message: string }>(
    '/api/v1/admin/competitions/research',
    { countries, target_tier },
    { headers: adminHeaders(email) },
  );
  return res.data;
}

export async function listProposals(
  email: string,
  params?: { status?: string; country?: string },
): Promise<CompetitionProposal[]> {
  const res = await api.get<CompetitionProposal[]>(
    '/api/v1/admin/competitions/proposals',
    { headers: adminHeaders(email), params },
  );
  return res.data;
}

export async function reviewProposal(
  id: string,
  body: { action: 'approve' | 'decline' | 'request_revision'; admin_notes?: string },
  email: string,
): Promise<{ proposal: CompetitionProposal; competition_id: string | null }> {
  const res = await api.patch<{ proposal: CompetitionProposal; competition_id: string | null }>(
    `/api/v1/admin/competitions/proposals/${id}`,
    body,
    { headers: adminHeaders(email) },
  );
  return res.data;
}

export async function getResearchTask(
  taskId: string,
  email: string,
): Promise<{ task: ResearchTask; proposal_count: number }> {
  const res = await api.get<{ task: ResearchTask; proposal_count: number }>(
    `/api/v1/admin/competitions/research/${taskId}`,
    { headers: adminHeaders(email) },
  );
  return res.data;
}

export default api;
